import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBolt,
  FaBookOpen,
  FaBullseye,
  FaCheck,
  FaCheckCircle,
  FaCode,
  FaCommentDots,
  FaProjectDiagram,
  FaShieldAlt,
  FaTrophy,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import type { HomeWhyId } from '../config/homeWhyIcons.ts';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import './css/HomeWhySection.css';

type WhyTabConfig = {
  id: HomeWhyId;
  icon: IconType;
  iconTone?: 'gold';
  ctaTo: string;
  featureIcons: IconType[];
};

const WHY_TABS: WhyTabConfig[] = [
  {
    id: 'interactive',
    icon: FaCode,
    ctaTo: '/problems',
    featureIcons: [FaBolt, FaBullseye, FaCommentDots, FaShieldAlt],
  },
  {
    id: 'autocheck',
    icon: FaCheckCircle,
    ctaTo: '/problems',
    featureIcons: [FaBolt, FaBullseye, FaCommentDots, FaShieldAlt],
  },
  {
    id: 'theory',
    icon: FaBookOpen,
    ctaTo: '/theory',
    featureIcons: [FaBolt, FaBullseye, FaCommentDots, FaShieldAlt],
  },
  {
    id: 'progress',
    icon: FaTrophy,
    iconTone: 'gold',
    ctaTo: '/leaderboard',
    featureIcons: [FaBolt, FaBullseye, FaCommentDots, FaShieldAlt],
  },
  {
    id: 'quests',
    icon: FaProjectDiagram,
    ctaTo: '/problems',
    featureIcons: [FaBolt, FaBullseye, FaCommentDots, FaShieldAlt],
  },
];

function WhyInteractiveMockup() {
  return (
    <div className="home-why-mockup" aria-hidden>
      <div className="home-why-mockup-editor">
        <div className="home-why-mockup-editor-bar">
          <span className="home-why-mockup-dot home-why-mockup-dot--r" />
          <span className="home-why-mockup-dot home-why-mockup-dot--y" />
          <span className="home-why-mockup-dot home-why-mockup-dot--g" />
          <span className="home-why-mockup-lang">Python</span>
        </div>
        <pre className="home-why-mockup-code">
          <code>
            <span className="tok-kw">def</span> <span className="tok-fn">is_prime</span>(n: <span className="tok-type">int</span>) -&gt; <span className="tok-type">bool</span>:{'\n'}
            {'    '}<span className="tok-kw">if</span> n &lt; <span className="tok-num">2</span>:{'\n'}
            {'        '}<span className="tok-kw">return</span> <span className="tok-bool">False</span>{'\n'}
            {'    '}<span className="tok-kw">for</span> i <span className="tok-kw">in</span> <span className="tok-fn">range</span>(<span className="tok-num">2</span>, <span className="tok-fn">int</span>(n ** <span className="tok-num">0.5</span>) + <span className="tok-num">1</span>):{'\n'}
            {'        '}<span className="tok-kw">if</span> n % i == <span className="tok-num">0</span>:{'\n'}
            {'            '}<span className="tok-kw">return</span> <span className="tok-bool">False</span>{'\n'}
            {'    '}<span className="tok-kw">return</span> <span className="tok-bool">True</span>
          </code>
        </pre>
      </div>
      <div className="home-why-mockup-tests">
        <div className="home-why-mockup-tests-head">
          <span>Тесты</span>
          <span className="home-why-mockup-tests-pass">
            <span className="home-why-mockup-pass-dot" /> 3 / 3 пройдено
          </span>
        </div>
        <ul className="home-why-mockup-test-list">
          <li><FaCheck aria-hidden /> is_prime(2) == True</li>
          <li><FaCheck aria-hidden /> is_prime(17) == True</li>
          <li><FaCheck aria-hidden /> is_prime(4) == False</li>
        </ul>
      </div>
      <div className="home-why-mockup-success">
        <span className="home-why-mockup-success-icon"><FaCheck aria-hidden /></span>
        <span>Все тесты пройдены! Отличная работа 🚀</span>
      </div>
    </div>
  );
}

function WhyAutocheckMockup() {
  return (
    <div className="home-why-mockup home-why-mockup--autocheck" aria-hidden>
      <div className="home-why-mockup-tests home-why-mockup-tests--solo">
        <div className="home-why-mockup-tests-head">
          <span>Результаты проверки</span>
          <span className="home-why-mockup-tests-pass">
            <span className="home-why-mockup-pass-dot" /> Accepted
          </span>
        </div>
        <ul className="home-why-mockup-test-list">
          <li><FaCheck aria-hidden /> Публичные тесты — 5/5</li>
          <li><FaCheck aria-hidden /> Скрытые тесты — 12/12</li>
          <li><FaCheck aria-hidden /> Время: 42 ms</li>
          <li><FaCheck aria-hidden /> Память: 14.2 MB</li>
        </ul>
      </div>
      <div className="home-why-mockup-success">
        <span className="home-why-mockup-success-icon"><FaCheck aria-hidden /></span>
        <span>Решение прошло все тесты</span>
      </div>
    </div>
  );
}

function WhyTheoryMockup() {
  return (
    <div className="home-why-mockup home-why-mockup--theory" aria-hidden>
      <div className="home-why-mockup-article">
        <div className="home-why-mockup-article-tag">Теория</div>
        <div className="home-why-mockup-article-title">Бинарный поиск</div>
        <div className="home-why-mockup-article-lines">
          <span /><span /><span className="short" />
        </div>
      </div>
      <div className="home-why-mockup-editor home-why-mockup-editor--compact">
        <div className="home-why-mockup-editor-bar">
          <span className="home-why-mockup-lang">Пример</span>
        </div>
        <pre className="home-why-mockup-code">
          <code>
            <span className="tok-kw">def</span> <span className="tok-fn">search</span>(arr, x):{'\n'}
            {'    '}lo, hi = <span className="tok-num">0</span>, len(arr){'\n'}
            {'    '}<span className="tok-kw">while</span> lo &lt;= hi: ...
          </code>
        </pre>
      </div>
    </div>
  );
}

function WhyProgressMockup() {
  return (
    <div className="home-why-mockup home-why-mockup--progress" aria-hidden>
      <div className="home-why-mockup-stats">
        <div className="home-why-mockup-stat">
          <span className="home-why-mockup-stat-val">128</span>
          <span className="home-why-mockup-stat-lbl">решено</span>
        </div>
        <div className="home-why-mockup-stat">
          <span className="home-why-mockup-stat-val">14</span>
          <span className="home-why-mockup-stat-lbl">серия дней</span>
        </div>
        <div className="home-why-mockup-stat">
          <span className="home-why-mockup-stat-val">#42</span>
          <span className="home-why-mockup-stat-lbl">рейтинг</span>
        </div>
      </div>
      <div className="home-why-mockup-chart">
        {[40, 55, 48, 72, 65, 80, 92].map((h, i) => (
          <span key={i} style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

function WhyQuestsMockup() {
  return (
    <div className="home-why-mockup home-why-mockup--quests" aria-hidden>
      <ol className="home-why-mockup-quest-list">
        <li className="done"><FaCheck aria-hidden /> Шаг 1 — SQL-запрос</li>
        <li className="done"><FaCheck aria-hidden /> Шаг 2 — Агрегация</li>
        <li className="active"><span className="home-why-mockup-quest-num">3</span> Шаг 3 — Визуализация</li>
        <li><span className="home-why-mockup-quest-num">4</span> Шаг 4 — Отчёт</li>
      </ol>
    </div>
  );
}

const MOCKUPS: Record<HomeWhyId, () => ReactElement> = {
  interactive: WhyInteractiveMockup,
  autocheck: WhyAutocheckMockup,
  theory: WhyTheoryMockup,
  progress: WhyProgressMockup,
  quests: WhyQuestsMockup,
};

export function HomeWhySection() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<HomeWhyId>('interactive');
  const active = WHY_TABS.find((tab) => tab.id === activeId) ?? WHY_TABS[0];
  const Mockup = MOCKUPS[activeId];

  const featureCount = 4;

  return (
    <section className="home-why-section" id="about">
      <div className="home-container">
        <header className="home-why-header">
          <h2 className="home-why-title">
            {t('homePage.whyTitleBefore')}
            <span className="home-why-title-accent">{t('homePage.whyTitleAccent')}</span>
          </h2>
          <p className="home-why-subtitle">{t('homePage.whySubtitle')}</p>
        </header>

        <div className="home-why-layout">
          <div className="home-why-tabs" role="tablist" aria-label={t('homePage.whyTitleAccent')}>
            {WHY_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = tab.id === activeId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`home-why-tab${isActive ? ' home-why-tab--active' : ''}`}
                  onClick={() => setActiveId(tab.id)}
                >
                  <span className={`home-why-tab-icon${tab.iconTone === 'gold' ? ' home-why-tab-icon--gold' : ''}`}>
                    <Icon aria-hidden />
                  </span>
                  <span className="home-why-tab-text">
                    <span className="home-why-tab-title">{t(`homePage.why${capitalize(tab.id)}`)}</span>
                    <span className="home-why-tab-desc">{t(`homePage.why${capitalize(tab.id)}TabDesc`)}</span>
                  </span>
                  <FaArrowRight className="home-why-tab-arrow" aria-hidden />
                </button>
              );
            })}
          </div>

          <div className="home-why-panel" role="tabpanel">
            <h3 className="home-why-panel-title">{t(`homePage.why${capitalize(active.id)}`)}</h3>
            <p className="home-why-panel-desc">{t(`homePage.why${capitalize(active.id)}LongDesc`)}</p>

            <div className="home-why-panel-body">
              <div className="home-why-panel-features">
                <ul className="home-why-feature-list">
                  {Array.from({ length: featureCount }, (_, i) => {
                    const FeatureIcon = active.featureIcons[i];
                    const n = i + 1;
                    return (
                      <li key={n} className="home-why-feature-item">
                        <span className="home-why-feature-icon">
                          <FeatureIcon aria-hidden />
                        </span>
                        <span className="home-why-feature-text">
                          <strong>{t(`homePage.why${capitalize(active.id)}Feat${n}Title`)}</strong>
                          <span>{t(`homePage.why${capitalize(active.id)}Feat${n}Desc`)}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <Link to={active.ctaTo} className="home-why-cta">
                  {t('homePage.whyStartPractice')}
                  <FaArrowRight aria-hidden />
                </Link>
              </div>
              <Mockup />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function capitalize(id: HomeWhyId): string {
  return id.charAt(0).toUpperCase() + id.slice(1);
}
